<template lang="pug">
section.microservice-object-form
    h4 {{ config.name }}
    p.hint {{ $t('Takes effect when you click the button below. Save does not include these fields.') }}
    p.problem(v-if="error") {{ error }}
    p.problem(v-else-if="!startAction") {{ $t('Enter the microservice URLs above and save the settings first.') }}
    //- same state display as FiskalyConfiguration
    el-descriptions.summary(
        v-if="summary.length"
        :column="1"
        :label-style="{ width: '1%', whiteSpace: 'nowrap' }"
        border
        size="small"
    )
        el-descriptions-item(
            v-for="row in summary"
            :key="row.key"
            :label="row.label || row.key"
        ) {{ row.value }}
    el-form.dynamic-form(v-if="startAction" label-position="top" @submit.native.prevent="start")
        .fields
            InputField(
                v-for="field in config.fields"
                :key="field.key"
                :config="field"
                :form="form"
                :error="fieldErrors[field.key]"
            )
        el-button(
            type="primary"
            native-type="submit"
            :loading="submitting"
        ) {{ $t('Apply {name}', { name: config.name }) }}
    //- same surface as ConfirmDeleteDialog, without "don't ask again": the
    //- microservice asks when the action replaces something
    el-dialog(
        v-focus-trap="confirmVisible"
        :title="$t('Apply {name}?', { name: config.name })"
        :visible.sync="confirmVisible"
        :close-on-click-modal="false"
        append-to-body
        top="15vh"
        width="420px"
    )
        p.confirmation {{ confirmation }}
        template(slot="footer")
            el-button(size="mini" icon="el-icon-fa-times fas" @click="confirmVisible = false") {{ $t('Cancel') }}
            el-button(size="mini" type="primary" icon="el-icon-fa-check fas" data-autofocus @click="submit") {{ $t('Apply') }}
</template>

<i18n>
{
    "en": {
        "Takes effect when you click the button below. Save does not include these fields.": "Takes effect when you click the button below. Save does not include these fields.",
        "Apply {name}": "Apply {name}",
        "Apply {name}?": "Apply {name}?",
        "Enter the microservice URLs above and save the settings first.": "Enter the microservice URLs above and save the settings first.",
        "Apply": "Apply",
        "Cancel": "Cancel",
        "{name} could not be loaded.": "{name} could not be loaded.",
        "{name} could not be saved.": "{name} could not be applied.",
        "{name} has been saved.": "{name} has been applied."
    },
    "de": {
        "Takes effect when you click the button below. Save does not include these fields.": "Gilt sofort mit dem Button unten. Speichern übernimmt diese Felder nicht.",
        "Apply {name}": "{name} übernehmen",
        "Apply {name}?": "{name} übernehmen?",
        "Enter the microservice URLs above and save the settings first.": "Tragen Sie zuerst die Microservice-URLs oben ein und speichern Sie die Einstellungen.",
        "Apply": "Übernehmen",
        "Cancel": "Abbrechen",
        "{name} could not be loaded.": "{name} konnte nicht geladen werden.",
        "{name} could not be saved.": "{name} konnte nicht übernommen werden.",
        "{name} has been saved.": "{name} wurde übernommen."
    }
}
</i18n>

<script>
import _ from 'lodash';

import MicroserviceApi from '@/store/api/system/microservice';
import InputField from '@/extensions/shop/views/Category/Partials/ProductTransferInputField';
import { resolveMicroserviceAction } from '@/helpers/microserviceAction';

/**
 * A form that is submitted to a microservice, configured in the template like
 * the shop import/export objects: `fields` + `initialize` (current state,
 * answered as `{ summary: [{ key, label, value }] }`) + `start` (the action).
 * The values go to the microservice only - never into the CMS settings.
 */
export default {
    name: 'MicroserviceObjectForm',
    components: {
        InputField,
    },
    props: {
        config: {
            type: Object,
            required: true,
        },
        // the CMS being edited and its settings - not necessarily the active CMS
        cmsId: {
            type: [Number, String],
            required: true,
        },
        settingValues: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        form: {},
        summary: [],
        // asked before start, when the microservice sends one with its state
        confirmation: null,
        confirmVisible: false,
        fieldErrors: {},
        error: false,
        submitting: false,
    }),
    computed: {
        cms() {
            return { id: parseInt(this.cmsId) };
        },
        startAction() {
            return resolveMicroserviceAction(this.config.start, this.settingValues);
        },
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.fieldErrors = {};
            },
        },
    },
    mounted() {
        this.resetForm();
        this.initialize();
    },
    methods: {
        resetForm() {
            (this.config.fields || []).forEach((field) => {
                const value = ['switch', 'checkbox'].includes(field.type)
                    ? ['true', '1', 'yes', true].includes(field.value)
                    : field.value;
                this.$set(this.form, field.key, value);
            });
        },
        async initialize() {
            const action = resolveMicroserviceAction(this.config.initialize, this.settingValues);
            if (!action) return;

            try {
                const { data } = await MicroserviceApi.REST(
                    action.url,
                    { cms: this.cms },
                    action.config
                );
                this.summary = _.get(data, 'summary') || [];
                this.confirmation = _.get(data, 'confirm') || null;
                this.error = false;
            } catch (error) {
                this.error = this.$t('{name} could not be loaded.', { name: this.config.name });
            }
        },
        start() {
            if (!this.startAction) return;

            if (this.confirmation) {
                this.confirmVisible = true;
            } else {
                this.submit();
            }
        },
        async submit() {
            this.confirmVisible = false;
            this.submitting = true;
            try {
                await MicroserviceApi.REST(
                    this.startAction.url,
                    { ...this.form, cms: this.cms },
                    this.startAction.config
                );
                this.$notify.success({
                    title: this.$t('{name} has been saved.', { name: this.config.name }),
                    iconClass: 'fas el-icon-fa-check',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
                this.resetForm();
                this.initialize();
            } catch (error) {
                // validation errors are shown under their fields
                const errors = _.get(error, 'response.data.errors');
                const fieldErrors = _.mapValues(errors || {}, (messages) => _.head(_.castArray(messages)));
                this.$notify.error({
                    title: this.$t('{name} could not be saved.', { name: this.config.name }),
                    message: errors ? undefined : _.get(error, 'response.data.message'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
                // after the toast: setting the errors must not be undone by the form watcher
                this.$nextTick(() => { this.fieldErrors = fieldErrors; });
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
// a form of its own inside the settings card: framed and tinted, with its own
// button, so nobody expects "Speichern" to submit it
.microservice-object-form
    width: 100%
    margin: 8px 0 18px
    padding: 16px 20px 20px
    background: $color-body
    border: 1px solid $--border-color-base
    border-radius: 6px

    h4
        margin: 0
        font-size: 1rem

    .hint
        margin: 4px 0 16px
        font-size: 13px
        color: $--color-text-secondary

    .problem
        margin: 0 0 16px
        font-size: 13px
        color: $--color-danger

    .summary
        margin-bottom: 20px

    .fields
        display: grid
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr))
        column-gap: 16px
        :deep(.el-input-number)
            width: 100%
            .el-input__inner
                text-align: left

.confirmation
    margin: 0
    line-height: 1.5
    color: $--color-text-regular
</style>
